import type { GridRowParams } from "@mui/x-data-grid";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "@tanstack/react-store";
import { LoadingWrapper } from "@vapor/react-contrib-tax-compliance";
import { EllipsisVertical } from "@vapor/react-icons";
import { Box } from "@vapor/react-material";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteTaxSubmission } from "../../../../../core/api/use-delete-tax-submission/useDeleteTaxSubmission";
import type { TaxSubmissionDTO } from "../../../../../core/api/use-get-tax-submissions/TaxSubmissionDTO";
import { useGetTaxSubmissions } from "../../../../../core/api/use-get-tax-submissions/useGetTaxSubmissions";
import { useSendRejectedTaxSubmission } from "../../../../../core/api/use-send-rejected-tax-submission/useSendRejectedTaxSubmission";
import { useUpdateTaxSubmissionAgencyData } from "../../../../../core/api/use-update-tax-submission-agency-data/useUpdateTaxSubmissionAgencyData";
import { store } from "../../../../../store/store";
import {
    SortableField,
    SortOrder,
    taxSubmissionsStoreSlice,
} from "../../../../../store/taxSubmissionSearchFiltersStoreSlice";
import { SendRejectedTaxSubmissionModal } from "../send-rejected-tax-submission-modal/SendRejectedTaxSubmissionModal";
import { TaxSubmissionDeleteModal } from "../tax-submission-delete-modal/TaxSubmissionDeleteModal";
import { UpdateAgencyDataModal } from "../tax-submission-detail-summary/update-agency-data-modal/UpdateAgencyDataModal";
import type { RowModel } from "./cellRenderers";
import { getRowModel } from "./cellRenderers";
import { useColumns } from "./useColumns";

interface TaxSubmissionsTableProps {
    onCheckNotSentTaxSubmissions: (
        taxSubmissions: TaxSubmissionDTO[],
    ) => unknown;
    onClickTaxSubmission: (taxSubmission: string) => unknown;
    onClickPreviewSogeiOutcome: (content: string, loading: boolean) => unknown;
    onClickRevokeTaxSubmission: (taxSubmissionId: string) => unknown;
}

export const TaxSubmissionsTable: React.FC<TaxSubmissionsTableProps> = ({
    onCheckNotSentTaxSubmissions,
    onClickTaxSubmission,
    onClickPreviewSogeiOutcome,
    onClickRevokeTaxSubmission,
}): JSX.Element => {
    const { t } = useTranslation();

    const filters = useSelector(
        store,
        (state) => state.taxSubmissionsSearchFilters,
    );

    const { data: taxSubmissions, isFetching: isLoadingTaxSubmissions } =
        useGetTaxSubmissions();

    const {
        isPending: loadingSendRejectedTaxSubmission,
        mutateAsync: sendRejectedTaxSubmission,
    } = useSendRejectedTaxSubmission();

    const {
        isPending: loadingUpdateAgencyData,
        mutateAsync: updateAgencyData,
    } = useUpdateTaxSubmissionAgencyData();

    const useDeleteTaxSubmissionMutation = useDeleteTaxSubmission();

    const [rejectedTaxSubmissionId, setRejectedTaxSubmissionId] =
        useState<string>();
    const [deleteTaxSubmissionId, setDeleteTaxSubmissionId] =
        useState<string>();
    const [
        updateAgencyDataTaxSubmissionId,
        setUpdateAgencyDataTaxSubmissionId,
    ] = useState<string>();

    const closeRejectedModal = useCallback(
        () => setRejectedTaxSubmissionId(undefined),
        [],
    );
    const closeUpdateAgencyDataModal = useCallback(
        () => setUpdateAgencyDataTaxSubmissionId(undefined),
        [],
    );
    const closeDeleteModal = useCallback(
        () => setDeleteTaxSubmissionId(undefined),
        [],
    );

    const onClickSendRejected = async () => {
        if (!rejectedTaxSubmissionId) return;
        await sendRejectedTaxSubmission({
            taxSubmissionId: rejectedTaxSubmissionId,
        });
    };

    const onClickDeleteTaxSubmission = async () => {
        if (!deleteTaxSubmissionId) return;
        await useDeleteTaxSubmissionMutation.mutateAsync({
            taxSubmissionId: deleteTaxSubmissionId,
        });
    };

    const handleOnUpdateAgencyDataClick = async (
        taxSubmissionId: string,
        agencyId: string,
        sendDate: Date,
    ) => {
        await updateAgencyData({ taxSubmissionId, agencyId, sendDate });
    };

    const onChangePaginationModel = (page: number, pageSize: number) => {
        taxSubmissionsStoreSlice.updateValues({ page, pageSize });
    };

    const columns = useColumns({
        onClickTaxSubmission,
        onClickRevokeTaxSubmission,
        onClickPreviewSogeiOutcome,
        setDeleteTaxSubmissionId,
        setRejectedTaxSubmissionId,
        setUpdateAgencyDataTaxSubmissionId,
    });

    return (
        <Box>
            <UpdateAgencyDataModal
                taxSubmissionId={updateAgencyDataTaxSubmissionId}
                open={!!updateAgencyDataTaxSubmissionId}
                onClose={closeUpdateAgencyDataModal}
                loading={loadingUpdateAgencyData}
                onEdit={handleOnUpdateAgencyDataClick}
            />
            <SendRejectedTaxSubmissionModal
                loading={loadingSendRejectedTaxSubmission}
                open={!!rejectedTaxSubmissionId}
                onClose={closeRejectedModal}
                onSend={onClickSendRejected}
            />
            <TaxSubmissionDeleteModal
                loading={useDeleteTaxSubmissionMutation.isPending}
                open={!!deleteTaxSubmissionId}
                onClose={closeDeleteModal}
                onDelete={onClickDeleteTaxSubmission}
            />

            {/* Loading Wrapper is necessary to make pagination work properly, as DataGrid would show the previous page data while loading the new page data without it */}
            <LoadingWrapper
                loading={isLoadingTaxSubmissions}
                variant="backdrop"
            >
                <DataGrid
                    slots={{
                        moreActionsIcon: (props) => (
                            <EllipsisVertical {...props} color="interactive" />
                        ),
                    }}
                    autoHeight
                    checkboxSelection
                    columns={columns}
                    disableRowSelectionOnClick
                    disableColumnFilter
                    disableColumnMenu
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: filters.pageSize,
                                page: filters.page,
                            },
                        },
                    }}
                    isRowSelectable={(params: GridRowParams<RowModel>) =>
                        params.row.isSendable
                    }
                    loading={isLoadingTaxSubmissions}
                    localeText={{
                        noRowsLabel: t(
                            "views.home.components.tax_submissions_table.no_data",
                        ),
                    }}
                    onPaginationModelChange={(model) =>
                        onChangePaginationModel(model.page, model.pageSize)
                    }
                    onRowSelectionModelChange={(taxSubmissionIds) => {
                        onCheckNotSentTaxSubmissions(
                            taxSubmissions.data.filter((taxSubmission) =>
                                taxSubmissionIds.includes(taxSubmission.id),
                            ),
                        );
                    }}
                    pageSizeOptions={[filters.pageSize]}
                    paginationMode="server"
                    pagination
                    rows={taxSubmissions.data.map(getRowModel)}
                    rowCount={taxSubmissions.totalCount}
                    sortingMode="server"
                    sortModel={
                        filters.sort
                            ? [
                                  {
                                      field: filters.sort.field,
                                      sort: filters.sort.order,
                                  },
                              ]
                            : []
                    }
                    onSortModelChange={(sortModel) => {
                        if (sortModel.length > 0) {
                            const sortItem = sortModel[0];
                            const field = sortItem.field as SortableField;
                            const order = sortItem.sort as SortOrder;

                            if (
                                [
                                    SortableField.DEADLINE,
                                    SortableField.SEND_DATE,
                                    SortableField.STATUS,
                                ].includes(field)
                            ) {
                                taxSubmissionsStoreSlice.updateValues({
                                    sort: { field, order },
                                });
                            }
                        } else {
                            taxSubmissionsStoreSlice.updateValues({
                                sort: undefined,
                            });
                        }
                    }}
                    sx={{
                        border: "none",
                        width: `calc(100% - 10px)`, // width is a workaround to use the fluid column width of mui-x-datagrid
                    }}
                />
            </LoadingWrapper>
        </Box>
    );
};
